import {
  createChartFrame,
  resolveTitle,
  resolvePalette,
  clearSvg,
  getLayout,
  appendGrid,
  appendAxes,
  addLabel,
  renderLegend,
  createSvgEl,
  scaleLinear,
  toNumber,
  normalizeSeriesColors
} from './chart-utils.js';

const DEFAULT_SERIES = [
  {
    name: 'Cluster A',
    points: [
      { x: 1, y: 2 },
      { x: 2, y: 3 },
      { x: 3, y: 2.2 },
      { x: 4, y: 3.4 },
      { x: 5, y: 2.6 }
    ]
  },
  {
    name: 'Cluster B',
    points: [
      { x: 1.4, y: 4.2 },
      { x: 2.6, y: 4.4 },
      { x: 3.6, y: 5.1 },
      { x: 4.4, y: 4.6 },
      { x: 5.3, y: 5.2 }
    ]
  }
];

export function render({ node }) {
  const { root, svg, legend, titleEl } = createChartFrame(node, 'Scatter Chart');

  const update = (next) => {
    titleEl.textContent = resolveTitle(next, 'Scatter Chart');
    const palette = resolvePalette(next);
    let series = normalizeSeriesColors(resolveSeries(next), palette);

    clearSvg(svg);
    const layout = getLayout();
    appendGrid(svg, layout);
    appendAxes(svg, layout);

    const pointsBySeries = series.map((entry) => normalizePoints(entry.points));
    const bounds = computeBounds(pointsBySeries, next);
    const radius = toNumber(next?.props?.radius ?? next?.data?.radius, 3);

    pointsBySeries.forEach((points, index) => {
      const color = series[index].color;
      points.forEach((pt) => {
        const x = scaleLinear(pt.x, bounds.xMin, bounds.xMax, layout.inner.x, layout.inner.x + layout.inner.w);
        const y = scaleLinear(pt.y, bounds.yMin, bounds.yMax, layout.inner.y + layout.inner.h, layout.inner.y);
        const dot = createSvgEl('circle', {
          cx: x,
          cy: y,
          r: radius,
          fill: color,
          'fill-opacity': 0.75
        });
        dot.classList.add('ew-chart-dot');
        svg.appendChild(dot);
      });
    });

    addAxisLabels(svg, layout, bounds);
    renderLegend(legend, series, { show: next?.props?.legend !== false });
  };

  update(node);
  return { el: root, update };
}

function resolveSeries(node) {
  const raw = node?.data?.series ?? node?.props?.series;
  const points = node?.data?.points ?? node?.props?.points;

  if (Array.isArray(raw)) {
    if (raw.length && raw[0] && typeof raw[0] === 'object' && raw[0].x !== undefined) {
      return [{ name: node?.props?.label || 'Series', points: raw }];
    }
    if (raw.length && raw[0] && typeof raw[0] === 'object') {
      return raw.map((entry, i) => ({
        name: entry.name || `Series ${i + 1}`,
        color: entry.color,
        points: Array.isArray(entry.points) ? entry.points : []
      }));
    }
  }

  if (Array.isArray(points)) {
    return [{ name: node?.props?.label || 'Series', points }];
  }

  return DEFAULT_SERIES;
}

function normalizePoints(points) {
  if (!Array.isArray(points)) return [];
  return points.map((pt, idx) => ({
    x: toNumber(pt.x ?? idx, idx),
    y: toNumber(pt.y, 0)
  }));
}

function computeBounds(seriesPoints, node) {
  let xMin = Infinity;
  let xMax = -Infinity;
  let yMin = Infinity;
  let yMax = -Infinity;

  seriesPoints.forEach((points) => {
    points.forEach((pt) => {
      if (pt.x < xMin) xMin = pt.x;
      if (pt.x > xMax) xMax = pt.x;
      if (pt.y < yMin) yMin = pt.y;
      if (pt.y > yMax) yMax = pt.y;
    });
  });

  if (!Number.isFinite(xMin)) xMin = 0;
  if (!Number.isFinite(xMax)) xMax = xMin + 1;
  if (!Number.isFinite(yMin)) yMin = 0;
  if (!Number.isFinite(yMax)) yMax = yMin + 1;

  const minXOverride = toNumber(node?.props?.minX ?? node?.data?.minX, NaN);
  const maxXOverride = toNumber(node?.props?.maxX ?? node?.data?.maxX, NaN);
  const minYOverride = toNumber(node?.props?.minY ?? node?.data?.minY, NaN);
  const maxYOverride = toNumber(node?.props?.maxY ?? node?.data?.maxY, NaN);

  return {
    xMin: Number.isFinite(minXOverride) ? minXOverride : xMin,
    xMax: Number.isFinite(maxXOverride) ? maxXOverride : xMax,
    yMin: Number.isFinite(minYOverride) ? minYOverride : yMin,
    yMax: Number.isFinite(maxYOverride) ? maxYOverride : yMax
  };
}

function addAxisLabels(svg, layout, bounds) {
  addLabel(svg, {
    x: layout.inner.x,
    y: layout.inner.y - 6,
    text: formatValue(bounds.yMax),
    anchor: 'start'
  });
  addLabel(svg, {
    x: layout.inner.x,
    y: layout.inner.y + layout.inner.h + 14,
    text: formatValue(bounds.yMin),
    anchor: 'start'
  });
  addLabel(svg, {
    x: layout.inner.x,
    y: layout.inner.y + layout.inner.h + 14,
    text: formatValue(bounds.xMin),
    anchor: 'start',
    className: 'ew-chart-label ew-chart-label-x'
  });
  addLabel(svg, {
    x: layout.inner.x + layout.inner.w,
    y: layout.inner.y + layout.inner.h + 14,
    text: formatValue(bounds.xMax),
    anchor: 'end',
    className: 'ew-chart-label ew-chart-label-x'
  });
}

function formatValue(value) {
  if (!Number.isFinite(value)) return '0';
  if (Math.abs(value) >= 1000) return value.toFixed(0);
  if (Math.abs(value) >= 100) return value.toFixed(1);
  return value.toFixed(2).replace(/\.00$/, '');
}
